import { get, has, isEqual } from 'lodash';
import moment from 'moment';

import {
	MANDATE_TYPE_STANDING_ORDER,
	MANDATE_TYPE_BACS_CREDIT,
	MANDATE_TYPE_BACS_DEBIT,
	dateFormat,
	MANDATE_TYPE_IFT,
} from '../config';
import { paymentFrequencyOptions } from '../config/paymentFrequencyOptions';
import {
	CONST_PICK_A_DATE,
	CONST_AFTER_NUMBER_TIMES,
	CONST_WHEN_I_CANCEL,
	CONST_INSTRUCTED,
	CONST_EQUIVALENT,
	CONST_AMOUNT_UPPERCASE,
} from '../config/constants';

const formatDate = (date) => moment(date).format(dateFormat);

export const getIFTCreditorDebtorAmount = (paymentAmount, debtorCurrency) => {
	const instructedAmount = paymentAmount.find((amount) => amount.type === CONST_INSTRUCTED);
	const equivalentAmount = paymentAmount.find((amount) => amount.type === CONST_EQUIVALENT);
	const isInstructorDebitorAmount = instructedAmount.currency === debtorCurrency;
	return {
		debtorAmount: isInstructorDebitorAmount ? instructedAmount : equivalentAmount,
		creditorAmount: !isInstructorDebitorAmount ? instructedAmount : equivalentAmount,
	};
};

export const getStopWhen = (endCondition) => {
	if (endCondition) {
		if (has(endCondition, 'when')) return CONST_PICK_A_DATE;
		if (has(endCondition, 'number_of_payments')) return CONST_AFTER_NUMBER_TIMES;
	}
	return CONST_WHEN_I_CANCEL;
};

const formatStandingOrderToView = (mandate) => {
	const {
		when,
		schedule: {
			end_condition,
			recurrence: { frequency },
		},
	} = mandate;
	const { label = '' } = paymentFrequencyOptions.find((frequencyOption) => isEqual(frequencyOption.request, frequency));
	return {
		...mandate,
		frequency: label,
		when: formatDate(when),
		numberOfTimes: get(end_condition, 'number_of_payments'),
		endDate: get(end_condition, 'when'),
		stopWhen: getStopWhen(end_condition),
	};
};
const formatBacsCreditToView = (mandate) => ({
	...mandate,
	from: mandate.fromAccount,
	bacsId: mandate.bacs_id,
	submissionDate: moment(mandate.submission_date).format(dateFormat),
	settlementDate: moment(mandate.settlement_date).format(dateFormat),
	numberOfPayments: get(mandate, 'entries', []).length,
	payments: get(mandate, 'entries', []).map((entry) => ({
		...entry,
		to: {
			displayName: get(entry, 'account.name'),
			accountNumber: get(entry, 'account.account_number'),
			sortCode: get(entry, 'account.sort_code'),
		},
		amount: get(entry, 'amount.value'),
		hmrcReference: get(entry, 'hmrc_reference'),
	})),
});

const formatBacsDebitToView = (mandate) => ({
	...mandate,
	to: mandate.fromAccount,
	bacsId: mandate.bacs_id,
	numberOfPayments: get(mandate, 'entries', []).length,
	submissionDate: moment(mandate.submission_date).format(dateFormat),
	settlementDate: moment(mandate.settlement_date).format(dateFormat),
	payments: get(mandate, 'entries', []).map((entry) => ({
		...entry,
		from: {
			displayName: get(entry, 'account.name'),
			accountNumber: get(entry, 'account.account_number'),
			sortCode: get(entry, 'account.sort_code'),
		},
		amount: get(entry, 'amount.value'),
		transactionCode: get(entry, 'transaction_code'),
	})),
});

const formatIFTPaymentToView = (mandate) => {
	const { creditorAmount, debtorAmount } = getIFTCreditorDebtorAmount(
		get(mandate, 'payment_amount', []),
		get(mandate, 'from_account.currency')
	);
	const dealReference = get(mandate, 'deal_reference', '');
	return {
		...mandate,
		submissionDate: moment(mandate.submission_date).format(dateFormat),
		settlementDate: mandate.value_date && moment(mandate.value_date).format(dateFormat),
		exchangeRate: dealReference
			? `${get(mandate, 'indicative_fx.rate', '')} (${dealReference})`
			: get(mandate, 'indicative_fx.rate', ''),
		sendAmount: creditorAmount,
		debitAmount: debtorAmount,
		paymentFees: mandate.charge_amount.find((amount) => amount.type === CONST_AMOUNT_UPPERCASE),
	};
};

export const formatMandateToView = (mandate) => {
	const { mandateType } = mandate;
	switch (mandateType) {
		case MANDATE_TYPE_STANDING_ORDER:
			return formatStandingOrderToView(mandate);
		case MANDATE_TYPE_BACS_CREDIT:
			return formatBacsCreditToView(mandate);
		case MANDATE_TYPE_BACS_DEBIT:
			return formatBacsDebitToView(mandate);
		case MANDATE_TYPE_IFT:
			return formatIFTPaymentToView(mandate);
		default:
			return mandate;
	}
};
